import api from 'src/store/api'
import http from 'src/store/http'
import logger from 'src/plugins/logger'
import server from 'src/store/server'
import transform from 'src/utils/transform'

import Organization from 'src/internal/store/utils/organization'
import User from 'src/internal/store/utils/user'

import { fireanalytics, fireauth, firecollection } from 'src/store/firebase'

import actionNames from './actionNames'
import PATHS from '@/router/paths'
import { copyObject, createObject } from '@/utils/objects'

const actions = {
  eventInitialized(context, { category, description, label }) {
    fireanalytics.event('goal_initialization', {
      category,
      description,
      label: label || `${description} Requested`,
    })
  },
  eventCompleted(context, { category, description, label }) {
    fireanalytics.event('goal_completion', {
      category,
      description,
      label: label || `${description} Succeeded`,
    })
  },
  eventException(context, { category, description, error, label }) {
    fireanalytics.event('exception', {
      category,
      description,
      label: label || `${description} Failed with Error ${error.message}`,
    })
  },

  /**
   * Run at app's created event, this updates the store in response to auth state changes.
   * @param commit
   * @param dispatch
   * @returns {Unsubscribe}
   */
  watch({ commit, dispatch }) {
    const autoAuth = async (user) => dispatch('autoAuth', user)
    const errorCallback = (error) => {
      const { message = 'Unknown Watch Error' } = error

      dispatch('eventException', { category: 'Auth', description: 'Auto Auth', error })

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })
    }

    fireauth.onAuthStateChanged(autoAuth, errorCallback)
  },
  async watchDoc({ commit }, { collectionName, docId, watcher }) {
    const observer = {
      error: (e) => {
        const { message = 'Unknown error on watch' } = e

        commit('tools/snackbar', true, { root: true })
        commit('tools/snack', { message, status: 'error' }, { root: true })

        return e
      },
      next: watcher,
    }

    return firecollection.watch(collectionName, docId, observer)
  },
  unwatchUser({ commit, state }) {
    if (typeof state.fnUnwatchUser === 'function') {
      state.fnUnwatchUser()
    }

    commit('fnUnwatchUser', null)
  },
  async watchUser({ commit, dispatch, state }, docId) {
    const watcher = async (response) => {
      const firestoreUser = response.data()

      if (firestoreUser) firestoreUser.docId = response.id

      const catcher = () => {}
      const { firebaseAuthUser, googleProfile } = state

      const aliasAndSelf = await dispatch('makeAliasAndSelf', { catcher, firebaseAuthUser, firestoreUser, googleProfile })

      return dispatch('setUser', aliasAndSelf)
    }

    const fnUnwatchUser = await dispatch('watchDoc', { collectionName: 'person', docId, watcher })

    commit('fnUnwatchUser', fnUnwatchUser)
  },
  async watchReport({ commit, dispatch }, docId) {
    const watcher = (response) => {
      const firestoreRecord = response.data()

      if (firestoreRecord) firestoreRecord.docId = response.id

      return dispatch('setReport', firestoreRecord)
    }

    const fnUnwatch = await dispatch('watchDoc', { collectionName: 'report', docId, watcher })

    commit('fnUnwatchReport', { docId, fnUnwatch })
  },

  setReport({ getters, rootState }, firestoreReport) {
    const { userEmail } = getters
    const { reports } = rootState.report
    const { associationId, docId, ownerId, shares, scope, subId } = firestoreReport
    const isGlobal = () => scope === 'global' && associationId === 'all-associations' && subId === 'all-secondary' && ownerId === 'all-users' && 'global'
    const isTeam = () => scope === 'global' && associationId !== 'all-associations' && subId !== 'all-secondary' && ownerId === 'all-users' && 'team'
    const isShared = () => shares.indexOf(userEmail) !== -1 && ownerId !== userEmail && 'shared'
    const isOwner = () => ownerId === userEmail && 'owner'
    const groupKey = isGlobal() || isTeam() || isOwner() || isShared()

    const reportsInScope = reports[groupKey] || []
    const reportIndex = reportsInScope.findIndex((r) => r.docId === docId)

    firestoreReport.groupKey = groupKey

    reports[groupKey].splice(reportIndex, 1, firestoreReport)

    return reports
  },
  async setReports({ commit, dispatch }, { email, isInternalUser, reportCodes }) {
    const catcher = (error) => {
      const { message = 'Unknown error on setReports' } = error

      dispatch('eventException', { category: 'Auth', description: 'Set Reports', error })

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })

      return error
    }
    const userEmailAndGroups = [email]

    if (isInternalUser) {
      userEmailAndGroups.push('internal')
    }

    const globalReportPromise = dispatch('getGlobalReports', { catcher })
    const allSharedReportPromises = userEmailAndGroups.map((emailOrGroupName) => dispatch('getSharedReports', { catcher, emailOrGroupName }))
    const sharedReportPromise = Promise.all(allSharedReportPromises).then((reports) => reports.flat())
    const teamReportsPromise = Promise
      .all(reportCodes.map((reportCode) => {
        const [billingCode, teamId] = reportCode.split('/')
        return dispatch('getTeamReports', { billingCode, catcher, teamId })
      }))
      .then((reports) => reports.flat())
    const ownerReportPromise = dispatch('getOwnerReports', { catcher, email })

    const promiseArray = [globalReportPromise, sharedReportPromise, teamReportsPromise, ownerReportPromise]
    const reportResponses = await Promise.all(promiseArray)
    const [globalReports, sharedReports, teamReports, ownerReports] = reportResponses

    const reportsByGroup = {
      global: globalReports.map((report) => {
        report.groupKey = 'global'
        return report
      }),
      owner: ownerReports.map((report) => {
        report.groupKey = 'owner'
        return report
      }),
      shared: sharedReports.map((report) => {
        report.groupKey = 'shared'
        return report
      }),
      team: teamReports.map((report) => {
        report.groupKey = 'team'
        return report
      }),
    }

    commit('report/reports', reportsByGroup, { root: true })

    Object.keys(reportsByGroup).forEach((groupKey) => {
      const reportsInGroup = reportsByGroup[groupKey]
      reportsInGroup.forEach((report) => dispatch('watchReport', report.docId))
    })
  },

  signInWithPopup({ commit, dispatch }) {
    commit(actionNames.SIGN_IN_REQUESTED)

    return fireauth.signInWithPopup().catch((error) => {
      if (error.code === 'auth/network-request-failed') {
        error.message = 'Network Unavailable'
      }

      const { message } = error

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })

      dispatch('eventException', { category: 'Auth', description: 'Sign In', error })
    })
  },

  async getUserByEmail(context, email) {
    return firecollection.get('person', email)
  },
  async getPersonDocuments(context, catcher) {
    return firecollection.collection('person', catcher)
  },
  async getExternalUsers(context, catcher) {
    const persons = await firecollection.collection('person', catcher)
    return persons.filter((person) => person.access && person.access.role === 'external')
  },

  async getReport(context, docId) {
    return firecollection.get('report', docId)
  },
  getGlobalReports(context, { catcher }) {
    const whereOwnerId = ['ownerId', '==', 'all-users']
    const whereAssociationId = ['associationId', '==', 'all-associations']
    const whereSubId = ['subId', '==', 'all-secondary']
    const whereScope = ['scope', '==', 'global']
    const whereClauses = firecollection.where([whereOwnerId, whereAssociationId, whereSubId, whereScope])
    return firecollection.findAll('report', whereClauses, catcher)
  },
  getTeamReports(context, { billingCode, catcher, teamId }) {
    const whereOwnerId = ['ownerId', '==', 'all-users']
    const whereAssociationId = ['associationId', '==', billingCode]
    const whereSubId = ['subId', '==', teamId]
    const whereScope = ['scope', '==', 'global']
    const whereClauses = firecollection.where([whereOwnerId, whereAssociationId, whereSubId, whereScope])
    return firecollection.findAll('report', whereClauses, catcher)
  },
  getOwnerReports(context, { catcher, email }) {
    const whereOwnerId = ['ownerId', '==', email]
    const whereClauses = firecollection.where([whereOwnerId])
    return firecollection.findAll('report', whereClauses, catcher)
  },
  getSharedReports(context, { catcher, emailOrGroupName }) {
    const whereShared = ['shares', 'array-contains', emailOrGroupName]
    const whereClauses = firecollection.where([whereShared])
    return firecollection.findAll('report', whereClauses, catcher)
  },

  async getMessages(context, email) {
    return firecollection.get('message', email)
  },

  async upsertFirestore(context, { collection, docId, doc }) {
    const options = { merge: true }
    return firecollection.set(collection, docId, doc, options)
  },
  async updateUserSettings({ dispatch }, { doc, docId }) {
    const collection = 'person'
    return dispatch('upsertFirestore', { collection, docId, doc })
  },
  async upsertUser({ dispatch }, personUser) {
    const collection = 'person'
    const doc = User.mapUser(personUser)
    const docId = personUser.email

    return dispatch('upsertFirestore', { collection, docId, doc })
  },
  async deleteUser({ dispatch }, personUser) {
    if (!personUser.access) {
      personUser.access = { active: false }
    } else {
      personUser.access.active = false
    }

    const collection = 'person'
    const doc = User.mapUser(personUser)
    const docId = personUser.email

    return dispatch('upsertFirestore', { collection, docId, doc })
  },

  handleUpdateToUserSettings({ commit, dispatch, getters, state }, { changes, fnSuccess }) {
    const { user } = state
    const { inAliasMode } = getters
    const currentSettings = inAliasMode ? getters.settings : getters.selfSettings
    const docId = inAliasMode && !changes.alias ? user.aliasId : user.docId

    const settings = Object.keys(changes).reduce((newObject, key) => {
      newObject[key] = changes[key]
      return newObject
    }, currentSettings)
    const doc = { settings }
    const dataToSubmit = { doc, docId }
    const fnSuccessDefault = () => {
      const message = 'Saved'
      const status = 'success'

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })
    }

    return dispatch('updateUserSettings', dataToSubmit)
      .then(fnSuccess || fnSuccessDefault)
      .catch((error) => {
        const { message = 'Unknown Error ' } = error
        const status = 'error'

        commit('tools/snackbar', true, { root: true })
        commit('tools/snack', { message, status }, { root: true })
      })
  },

  async updateAliasMode({ commit, dispatch, getters }, alias) {
    commit(actionNames.SET_ALIAS_MODE_REQUESTED)

    const isSettingAliasModeOn = (alias && alias.active && 'On') || 'Off'

    fireanalytics.event('goal_initialization', {
      category: 'Alias',
      description: `Set Alias Mode ${isSettingAliasModeOn}`,
      label: `Set Alias Mode ${isSettingAliasModeOn} Requested`,
    })

    const hasRightsToUpdate = getters.isAliasWriter

    const catcher = (err) => {
      const error = http.catch(err)
      const errorMessage = error.message || 'Unknown Error'

      // Update state with error.
      commit(actionNames.SET_ALIAS_MODE_FAILED, error)

      // Log error to analytics.
      fireanalytics.event('exception', {
        category: 'Alias',
        description: `Set Alias Mode ${isSettingAliasModeOn}`,
        label: `Set Alias Mode ${isSettingAliasModeOn} Failed with Error "${error.message}"`,
      })

      // Inform user of error.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    // If user is not authorized to update alias mode, return error.
    if (!hasRightsToUpdate) {
      const error = { code: '403', layer: 'app', message: 'Forbidden' }
      return catcher({ error })
    }

    // Update the alias settings on the user record.
    const fnSuccess = () => {
      const message = 'Updated Alias Settings'
      const status = 'success'

      // Inform user of success.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })

      // Update state with success.
      commit(actionNames.SET_ALIAS_MODE_SUCCEEDED, alias)

      // Log success to analytics.
      fireanalytics.event('goal_completion', {
        category: 'Alias',
        description: `Set Alias Mode ${isSettingAliasModeOn}`,
        label: `Set Alias Mode ${isSettingAliasModeOn} Succeeded`,
      })

      // Reload page to update view.
      window.location = '/'
    }

    const changes = { alias }

    return dispatch('handleUpdateToUserSettings', { changes, fnSuccess })
  },

  async setDemoMode({ commit }, newDemoMode) {
    commit('demoMode', newDemoMode)

    await api.set(newDemoMode)

    return newDemoMode
  },

  async updateDemoMode({ commit, dispatch, rootGetters }, demo) {
    commit(actionNames.SET_DEMO_MODE_REQUESTED)

    const isSettingDemoModeOn = demo === true || (typeof demo === 'object' && demo.active === true)
    const statusWord = isSettingDemoModeOn ? 'On' : 'Off'

    fireanalytics.event('goal_initialization', {
      category: 'Demo',
      description: `Set Demo Mode ${statusWord}`,
      label: `Set Demo Mode ${statusWord} Requested`,
    })

    const hasRightsToUpdate = rootGetters['auth/isDemoWriter']
    const catcher = (err) => {
      const error = http.catch(err)
      const errorMessage = error.message || 'Unknown Error'

      // Update state with error.
      commit(actionNames.SET_DEMO_MODE_FAILED, error)

      // Log error to analytics.
      fireanalytics.event('exception', {
        category: 'Demo',
        description: `Set Demo Mode ${statusWord}`,
        label: `Set Demo Mode ${statusWord} Failed with Error "${error.message}"`,
      })

      // Inform user of error.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    if (!hasRightsToUpdate) {
      const error = { code: '403', layer: 'app', message: 'Forbidden' }
      return catcher({ error })
    }

    const fnSuccess = async () => {
      commit(actionNames.SET_DEMO_MODE_SUCCEEDED, demo)

      fireanalytics.event('goal_completion', {
        category: 'Demo',
        description: `Set Demo Mode ${isSettingDemoModeOn}`,
        label: `Set Demo Mode ${isSettingDemoModeOn} Succeeded`,
      })

      await dispatch('setDemoMode', demo && typeof demo === 'object' && demo.active)

      window.location = '/'
    }

    const changes = { demo }

    return dispatch('handleUpdateToUserSettings', { changes, fnSuccess })
  },

  async updateDevMode({ commit, dispatch, getters }, dev) {
    commit(actionNames.SET_DEV_MODE_REQUESTED)

    const isSettingDevModeOn = (dev && dev.active && 'On') || 'Off'

    fireanalytics.event('goal_initialization', {
      category: 'Alias',
      description: `Set Alias Mode ${isSettingDevModeOn}`,
      label: `Set Alias Mode ${isSettingDevModeOn} Requested`,
    })

    const hasRightsToUpdate = getters.hasDevPermission

    const catcher = (err) => {
      const error = http.catch(err)
      const errorMessage = error.message || 'Unknown Error'

      // Update state with error.
      commit(actionNames.SET_DEV_MODE_FAILED, error)

      // Log error to analytics.
      fireanalytics.event('exception', {
        category: 'Dev Mode',
        description: `Set Dev Mode ${isSettingDevModeOn}`,
        label: `Set Dev Mode ${isSettingDevModeOn} Failed with Error "${error.message}"`,
      })

      // Inform user of error.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    // If user is not authorized to update alias mode, return error.
    if (!hasRightsToUpdate) {
      const error = { code: '403', layer: 'app', message: 'Forbidden' }
      return catcher({ error })
    }

    // Update the alias settings on the user record.
    const fnSuccess = () => {
      const message = 'Updated Dev Settings'
      const status = 'success'

      // Inform user of success.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })

      // Update state with success.
      commit(actionNames.SET_DEV_MODE_SUCCEEDED, dev)

      // Log success to analytics.
      fireanalytics.event('goal_completion', {
        category: 'Dev Mode',
        description: `Set Dev Mode ${isSettingDevModeOn}`,
        label: `Set Dev Mode ${isSettingDevModeOn} Succeeded`,
      })
    }

    const changes = { dev }

    return dispatch('handleUpdateToUserSettings', { changes, fnSuccess })
  },

  /**
   * Gets a Tableau token from the server.
   * @returns {ServerResponse}
   */
  async getTableauToken() {
    return server.generate('tableau')
  },

  async getOrganizations({ dispatch }, { params: routeParams, query: routeQuery, user }) {
    const endpointKey = 'organization'
    const isCurrentPageAnOrgReport = routeParams?.baseReportName === endpointKey

    const params = isCurrentPageAnOrgReport ? routeParams : { baseReportName: 'organization', routeName: 'internal' }
    const query = isCurrentPageAnOrgReport ? routeQuery : {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }

    return dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
  },
  async getTimeRollups({ dispatch }, { user }) {
    const endpointKey = PATHS.INTERNAL.TIME_ROLLUP
    const params = {}
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    return dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
  },
  /**
   * Gets persons from API, plus users from Store, to make PersonUsers.
   * @param {Object} context
   * @param {Function} context.dispatch
   * @param {Object} data
   * @param {User} data.user
   */
  async getPersonUsers({ dispatch }, { user }) {
    const endpointKey = 'person'
    const params = {}
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    return dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
  },
  async getInternalTeams({ dispatch }, { user }) {
    const endpointKey = 'internal-team'
    const params = {}
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    return dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
  },
  async getProjects({ dispatch }, { user }) {
    const endpointKey = 'project'
    const params = {}
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    const fetcher = () => dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
    return User.isInternalUser(user) ? fetcher() : []
  },
  async getServices({ dispatch }, { user }) {
    const endpointKey = 'service'
    const params = {}
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    const fetcher = () => dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
    return User.isInternalUser(user) ? fetcher() : []
  },
  async getTimes({ dispatch }, { user }) {
    const endpointKey = PATHS.INTERNAL.TIME_SHEET
    const params = {}
    const query = {}
    return dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
  },
  async getTimeTypes({ dispatch }, { user }) {
    const endpointKey = 'timeType'
    const params = {}
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    return dispatch('tools/getCollectionByName', { endpointKey, params, query, user }, { root: true })
  },

  async getAllTeams({ commit }, publisher) {
    const { publisherId } = publisher
    const teamsCatcher = () => commit('GET_TEAMS_FAILED', publisher)

    return api.getTeams(publisherId).catch(teamsCatcher)
  },
  findNetworkTeams(context, { billingCode, networks }) {
    const findConfiguredTeams = (network) => {
      const { organization, team: configuredTeams } = network
      return String(organization.code) === String(billingCode) ? configuredTeams : false
    }

    return networks.find(findConfiguredTeams) || []
  },
  hasAllTeamsSet(context, teams) {
    if (!Array.isArray(teams)) return false
    return !!teams.find((team) => team.id === '-1')
  },
  async getConfiguredTeams({ dispatch }, { publisher, networks }) {
    const { billingCode, publisherId } = publisher
    const publisherNetworkData = { billingCode, networks }
    const network = await dispatch('findNetworkTeams', publisherNetworkData)
    const { team: networkTeams } = network
    const hasAllTeamsSet = await dispatch('hasAllTeamsSet', networkTeams)
    const getDemoTeams = () => billingCode === 'DEMO2021' && import('src/store/stubs/teams.json')
    const getAllTeams = () => hasAllTeamsSet && dispatch('getAllTeams', publisher)
    const getTeamsFromApi = () => {
      const teamIds = networkTeams.map((networkTeam) => networkTeam.id)
      const content = api.getTeamsByPublisherId(publisherId, teamIds)

      return { content, error: false }
    }

    return getDemoTeams() || getAllTeams() || getTeamsFromApi()
  },
  async setTeamsOnPublisher({ dispatch }, requestData) {
    const { isInternalUser, publisher } = requestData
    const success = (teamsResponse) => {
      const { content: teams, error: teamError } = teamsResponse

      publisher.teams = teamError ? teamsResponse : teams

      return publisher
    }
    const getAllTeams = () => isInternalUser && dispatch('getAllTeams', publisher).then(success)
    const getConfiguredTeams = () => dispatch('getConfiguredTeams', requestData).then(success)

    return getAllTeams() || getConfiguredTeams()
  },
  setTeamsOnPublishers({ dispatch }, { isInternalUser, publishersResponse, user }) {
    const { network: networks = [{}] } = user
    const { content: publishers, error: publisherError } = publishersResponse

    return publisherError || Promise.all(publishers.map(async (publisher) => {
      const getPublisherWithoutTeams = () => {
        const { hasTeam } = publisher
        return !hasTeam && Promise.resolve(publisher)
      }
      const getPublisherWithTeams = () => {
        const requestData = { isInternalUser, publisher, networks }
        return dispatch('setTeamsOnPublisher', requestData)
      }

      return getPublisherWithoutTeams() || getPublisherWithTeams()
    }))
  },

  async getClients({ dispatch }, { billingCode, inDemoMode, isInternalUser }) {
    const endpointKey = 'client'
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    const catcher = (e) => {
      const code = 500
      const message = e.message || 'Error Unknown'

      return {
        code,
        error: true,
        layer: 'app',
        message,
      }
    }

    const getDemoClient = async () => import('src/store/stubs/clients.json')
    const getClients = async () => !inDemoMode && api.getClients(billingCode).catch(catcher)

    const response = await getClients()
    const { content: clients = [], error } = response || {}

    if (error) return catcher(response)

    if (isInternalUser) {
      const demoClients = await getDemoClient()
      const { content: demoClientsContent } = demoClients || { content: [] }

      clients.push(...demoClientsContent)
    }

    const items = clients.map((client) => {
      client.text = client.clientName
      client.value = client.clientId

      return client
    })

    return dispatch('tools/store', { endpointKey, items, query }, { root: true })
  },

  /**
   * Gets publishers and their teams from the API.
   * @param {Object} context
   * @param {function} context.dispatch
   * @param {Object} data
   * @param {boolean} data.isInternalUser
   * @param {Object} data.user { network, role }
   * @return {Promise<ApiResponse>}
   */
  fetchPublishersByRole({ dispatch }, { inDemoMode, isInternalUser, user }) {
    const setTeamsOnPublishers = async (publishersResponse) => {
      const teams = await dispatch('setTeamsOnPublishers', { isInternalUser, publishersResponse, user })
      return { content: teams, totalCount: teams.length }
    }
    const getDemoPublisher = async () => import('src/store/stubs/publishers.json')
    const getPublishers = () => (inDemoMode ? getDemoPublisher() : api.getPublishers())
    const internalRequest = async () => {
      const demoPublishersResponse = await getDemoPublisher()
      const realPublishersResponse = await getPublishers()
      const { content: demoPublishers } = demoPublishersResponse || {}
      const { content: realPublishers } = realPublishersResponse || {}

      if (demoPublishers && Array.isArray(demoPublishers)) {
        realPublishers.push(...demoPublishers)
      }

      const publishersResponse = { content: realPublishers, error: false }
      return setTeamsOnPublishers(publishersResponse)
    }
    const externalRequest = () => {
      const { network: networks = [{}] } = user
      const getPublishersByBillingCodes = () => {
        const hasInvalidNetworkConfiguration = !Array.isArray(networks) || networks.length === 0
        const getRealPublishers = () => {
          const billingCodes = networks.map((network) => {
            const { organization = {} } = network
            const { code = '' } = organization || {}
            // @TODO: Determine whether we should check for a valid billing code format before making this request.
            return typeof code === 'string' ? code : ''
          })

          return api.getPublishersByBillingCodes(billingCodes)
        }

        return hasInvalidNetworkConfiguration ? getDemoPublisher() : getRealPublishers()
      }
      const apiRequest = inDemoMode ? getDemoPublisher() : getPublishersByBillingCodes()

      return apiRequest.then(setTeamsOnPublishers)
    }

    return isInternalUser ? internalRequest() : externalRequest()
  },
  /**
   * Gets publishers and their teams from the API, then stores the results.
   * @param {Object} context
   * @param {function} context.dispatch
   * @param {object} context.rootGetters
   * @param {Object} data
   * @param {boolean} data.isInternalUser
   * @param {Object} data.user { network, role }
   * @return {Promise<Publishers|ErrorMessage>}
   */
  async getPublishers({ dispatch }, { inDemoMode, isInternalUser, user }) {
    const endpointKey = 'publisher'
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }
    const catcher = () => {}

    const response = await dispatch('fetchPublishersByRole', { inDemoMode, isInternalUser, user })
    const { content: items, error } = response
    const storePublishers = () => dispatch('tools/store', { endpointKey, items, query }, { root: true })

    return error ? catcher(error) : storePublishers()
  },
  getOrgConfig(context, catcher) {
    return firecollection.collection('organization', catcher)
  },
  getOrgConfigByBillingCode(context, { billingCodes, catcher }) {
    const promises = billingCodes.map((billingCode) => firecollection.get('organization', billingCode, catcher))

    return Promise.all(promises)
  },
  getOrgRequestsByRole({ commit, dispatch }, user) {
    const inDemoMode = User.inDemoMode(user)
    const isInternalUser = user.access?.role === 'internal'
    const { network: networks } = user
    const catcher = (err) => {
      const { message = err.error?.message } = err

      if (!err.metadata) err.metadata = { email: user.docId, message }

      const error = http.catch(err)

      const errorMessage = message || 'Error Unknown'

      // Replace error message's ephemeral snackbar with a persistent dialog.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    const getOrgConfigByBillingCode = () => {
      const billingCodes = networks && networks.reduce((billingCodesArray, network) => {
        const { organization: { code: billingCode } } = network || { organization: {} }
        if (!billingCodesArray.includes(billingCode)) billingCodesArray.push(billingCode)
        return billingCodesArray
      }, [])
      return dispatch('getOrgConfigByBillingCode', { billingCodes, catcher })
    }

    return {
      clients: isInternalUser ? dispatch('getClients', { inDemoMode, isInternalUser }) : [],
      configs: isInternalUser ? dispatch('getOrgConfig', catcher) : getOrgConfigByBillingCode(),
      projects: dispatch('auth/getProjects', { user }, { root: true }),
      publishers: dispatch('getPublishers', { inDemoMode, isInternalUser, user }),
    }
  },
  async fetchClientsAndPublishers({ dispatch }, user) {
    const requestsByRole = await dispatch('getOrgRequestsByRole', user)

    const { clients: clientsReq, configs, projects: projectsReq, publishers: publishersReq } = requestsByRole

    return Promise.all([publishersReq, clientsReq, configs, projectsReq, Promise.resolve(user)])
  },
  reduceClientsAndPublishers(context, { clients, configs, projects, publishers, user }) {
    if (user.access?.role === 'internal') {
      configs.forEach((config) => {
        config.isInternal = true
      })
    }

    const makeOrgs = Organization.reduceClientsAndPublishers.bind(Organization, configs, projects)
    const clientsAndPublishers = []

    clientsAndPublishers.push(...clients, ...publishers)

    return clientsAndPublishers.reduce(makeOrgs, []) // @TODO: This is where we should filter out inactive orgs.
  },
  async transformClientsAndPublishers({ commit, dispatch }, response) {
    const [publishers, clients, configs, projects, user] = response
    const catcher = (error) => {
      const { message = 'Unknown Transform Error' } = error

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })

      return error
    }

    if (!Array.isArray(clients)) return catcher({ code: 400, error: true, message: 'Error: Clients API Endpoint Response', metadata: { clients } })
    if (!Array.isArray(publishers)) return catcher({ code: 400, error: true, message: 'Error: Publishers API Endpoint Response', metadata: { publishers } })

    return dispatch('reduceClientsAndPublishers', { clients, configs, projects, publishers, user })
  },

  async fetchPersonAndUserById({ dispatch }, { catcher, personId }) {
    const personsRequest = api.getPersonById(personId).catch(catcher)
    const { personEmail } = await personsRequest

    const usersRequest = (personEmail && dispatch('getUserByEmail', personEmail).then((user) => [user])) || {
      code: 404,
      layer: 'app/data',
      message: 'User Not Found',
      metadata: { personId },
    }

    return [personsRequest, usersRequest]
  },
  async fetchPersonsAndUsers({ commit, dispatch }, { params, query, user }) {
    const { baseReportName } = params
    const { associationId: personId } = query
    const catcher = (error) => {
      const { message = 'Unknown error fetching person or user' } = error

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })

      return error
    }
    const havePersonId = () => personId && /^[0-9]+\b/.test(personId)
    const fetchPersonAndUserById = () => baseReportName === 'person' && havePersonId() && dispatch('fetchPersonAndUserById', { catcher, personId })
    const fetchPersons = async () => {
      const personsRequest = api.getPersons().catch(catcher)
      const usersRequest = dispatch('getPersonDocuments', catcher)

      return [personsRequest, usersRequest]
    }
    const personAndUserPromises = await fetchPersonAndUserById() || await fetchPersons()

    dispatch('getInternalTeams', { user })

    return Promise.all(personAndUserPromises)
  },
  async transformPersonUsers({ commit, rootGetters }, responses) {
    const [personsResponse, users] = responses

    const publishersAsTeams = rootGetters['tools/publishersAsTeams']
    const transformer = async (fetchedResponses) => {
      const personColumnsByKey = rootGetters['tools/personColumnsByKey']
      const [personsRes] = fetchedResponses
      const { content: persons } = personsRes

      const formatAndNormalize = (item) => transform.formatAndNormalize(item, personColumnsByKey)
      const personsByEmail = persons.reduce((personsDict, person) => {
        person.isUser = false
        person.isPerson = true

        const findManagerName = () => {
          const findManager = () => persons.find((p) => p.personId === person.managerPersonId)
          const manager = person.managerPersonId && findManager()
          return manager && `${manager.personNameFirst} ${manager.personNameLast}`
        }
        const nameManagerById = () => person.managerPersonId && `Person ID ${person.managerPersonId}`
        const nameManager = () => {
          const setManagerName = () => findManagerName() || nameManagerById() || 'None'
          return person.isInternal ? setManagerName() : 'N/A'
        }

        person.managerPersonName = nameManager()

        personsDict[person.personEmail] = person

        return personsDict
      }, {})
      const personUsersByEmail = users.reduce((personUsersDict, userDoc) => {
        const { docId: userEmail } = userDoc
        const matchingPersonRecord = personUsersDict[userEmail]

        if (matchingPersonRecord) {
          personUsersDict[userEmail] = { ...userDoc, ...matchingPersonRecord }
        } else {
          const defaultPerson = {
            isPerson: false,
            personId: null,
            personEmail: userEmail,
          }
          personUsersDict[userEmail] = { ...userDoc, ...defaultPerson }
        }

        personUsersDict[userEmail].isUser = true

        return personUsersDict
      }, personsByEmail)

      const personUsers = Object.keys(personUsersByEmail).map(async (emailAddress) => {
        const personUser = personUsersByEmail[emailAddress]
        const person = await User.mapPersonAndUser(personUser, publishersAsTeams)
        const formatAndNormalizePerson = (viewUser, key) => formatAndNormalize(person)(key)
        return Object.keys(person).reduce(formatAndNormalizePerson, person)
      })

      return Promise.all(personUsers)
    }

    const rules = [
      () => !users && 'firebase',
      () => !personsResponse && 'api',
      () => personsResponse.error && personsResponse,
    ]
    const foundError = rules.find((fnValidate) => fnValidate(personsResponse, users))
    const catcher = (error) => {
      const { message = 'Unknown Transform Error' } = error

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })

      return error
    }
    const catchError = () => foundError && catcher(foundError)

    return catchError() || transformer(responses)
  },

  async getPersonByEmail(context, { catcher, email }) {
    const response = await api.getPersonByEmail(email).catch(catcher)
    const { content, error } = response

    if (error || !Array.isArray(content)) return response

    const [person] = content

    return person
  },
  async setPerson({ commit, dispatch }, { catcher, email }) {
    const person = await dispatch('getPersonByEmail', { catcher, email })
    const isAuthorizedPerson = person && !person.error && User.isAuthorizedPerson(person)

    if (!isAuthorizedPerson) return catcher(person)

    commit('person', person)

    return person
  },

  async makeUser({ dispatch, rootGetters, state }, userRecord) {
    const { person } = state
    const params = {
      baseReportName: 'organization',
      routeName: 'internal',
    }
    const query = {
      associationId: 'all-associations',
      reportPath: 'all-data',
      subId: 'all-secondary',
    }

    await dispatch('getOrganizations', { params, query, user: userRecord })

    const publishersAsTeams = rootGetters['tools/publishersAsTeams']

    const personUser = { ...userRecord, ...person }

    return User.mapPersonAndUser(personUser, publishersAsTeams)
  },
  async makeSelf(context, userRecord) {
    const { docId } = userRecord
    const personResponse = await api.getPersonByEmail(docId)
    const { content, error } = personResponse

    if (error || !Array.isArray(content)) {
      return copyObject(userRecord)
    }

    const [person] = content

    return createObject(userRecord, person)
  },
  async setUser({ commit, dispatch }, userRecord) {
    const user = await dispatch('makeUser', userRecord)
    const { isInternalUser, reportCodes } = user
    const email = user.aliasId || user.docId // aliasId is defined by the logic in the User.aliasId method.

    if (user.self) {
      const self = await dispatch('makeUser', user.self)
      commit('self', self)
    }

    await dispatch('setReports', { email, isInternalUser, reportCodes })

    commit('user', user)

    return user
  },

  async makeAnalyticsUser({ rootGetters }, personUser) {
    const { network } = personUser
    const networks = Organization.flattenUserNetworks(network)
    const { billingCodes, orgIds, reportCodes, teamCodes, teamIds } = networks

    const hasPacing = await rootGetters['tools/hasPacing']
    const hasDiscrepancy = await rootGetters['tools/hasDiscrepancy']
    const hasHealthAdx = await rootGetters['tools/hasHealthAdx']
    const hasHealthNetwork = await rootGetters['tools/hasHealthNetwork']
    const hasProgrammatic = await rootGetters['tools/hasProgrammatic']
    const hasYieldPartner = await rootGetters['tools/hasYieldPartner']
    const inDemoMode = rootGetters['auth/inDemoMode']

    const {
      access,
      expires,
      isDeleted,
      managerPersonId,
      personId,
    } = personUser
    const { active, level, role } = access || {}
    const version = process.env.VUE_APP_VERSION

    return {
      active,
      billingCodes: billingCodes.join(', '),
      expires,
      hasDiscrepancy,
      hasHealthAdx,
      hasHealthNetwork,
      hasProgrammatic,
      hasYieldPartner,
      hasPacing,
      isDeleted,
      inDemoMode,
      level,
      managerPersonId,
      orgIds: orgIds.join(', '),
      personId,
      reportCodes: reportCodes.join(', '),
      role,
      teamCodes: teamCodes.join(', '),
      teamIds: teamIds.join(', '),
      version,
    }
  },
  async setSentryMonitoring(context, { analyticsUser, personId }) {
    const { billingCodes, inDemoMode, level, reportCodes, role, teamCodes } = analyticsUser
    const { hostname } = window.document.location
    const nodeEnv = process.env.NODE_ENV
    const project = process.env.VUE_APP_GOOGLE_PROJECT
    const version = process.env.VUE_APP_VERSION

    logger.sentry.setUser({ billingCodes, inDemoMode, level, personId, reportCodes, role, teamCodes })
    logger.sentry.setTags({ hostname, nodeEnv, project, version })

    return analyticsUser
  },
  setGoogleAnalytics(context, analyticsData) {
    return fireanalytics.setUser(analyticsData)
  },
  async monitorUser({ dispatch }, personUser) {
    const analyticsUser = await dispatch('makeAnalyticsUser', personUser)
    const { personId = personUser.docId } = personUser
    const analyticsData = { analyticsUser, personId }

    dispatch('watchUser', personUser.email)
    dispatch('setGoogleAnalytics', analyticsData)
    dispatch('setSentryMonitoring', analyticsData)

    return analyticsUser
  },

  async grantApiAccess(context, { firebaseAuthUser, user }) {
    const token = await firebaseAuthUser.getIdToken()

    http.setRequester(user.docId)
    http.setAuthorizationHeader(token)

    return firebaseAuthUser
  },
  async setHttpRequests({ dispatch }, { firebaseAuthUser, user }) {
    const inDemoMode = User.inDemoMode(user)

    await dispatch('setDemoMode', inDemoMode)

    return dispatch('grantApiAccess', { firebaseAuthUser, user })
  },

  async mapAliasAndSelf(context, { catcher, user }) {
    const aliasId = user.aliasId || user.docId
    const aliasUser = await firecollection.get('person', aliasId, catcher)

    user.self = {
      access: copyObject(user.access),
      aliasId,
      docId: user.docId,
      email: user.email, // @usedBy selfEmail getter in store/auth
      network: copyObject(user.network),
      settings: copyObject(user.settings),
    }

    user.access = aliasUser.access || {}
    user.network = aliasUser.network || []
    user.settings = aliasUser.settings || {}

    // To emulate users before activating them, we ignore alias.access.active.
    user.access.active = true

    // The docId/email is untouched because calls to the API require it.
    return user
  },
  async makeAliasAndSelf({ dispatch }, { catcher, firebaseAuthUser, firestoreUser, googleProfile }) {
    const aliasId = User.aliasId(firestoreUser)
    const fireUser = { ...firebaseAuthUser, ...firestoreUser, ...googleProfile, aliasId }
    const mapAliasAndSelf = () => dispatch('mapAliasAndSelf', { catcher, user: fireUser })

    return (aliasId && await mapAliasAndSelf()) || fireUser
  },

  async makeAndStoreUser({ dispatch, state }, catcher) {
    const { firebaseAuthUser, firestoreUser, googleProfile } = state
    const errors = User.authorizeUser(firestoreUser)

    if (errors.length > 0) {
      const metadata = { email: firestoreUser.docId, errors }
      const error = { code: '403', layer: 'app', message: 'Access Denied', metadata }

      return catcher({ error })
    }

    const aliasAndSelf = await dispatch('makeAliasAndSelf', { catcher, firebaseAuthUser, firestoreUser, googleProfile })

    await dispatch('setHttpRequests', { firebaseAuthUser, user: aliasAndSelf })

    const person = await dispatch('setPerson', { catcher, email: firestoreUser.docId })
    const user = await dispatch('setUser', aliasAndSelf)

    const personError = () => person.error && catcher(person)
    const userError = () => user.error && catcher(user)

    const monitorUser = () => dispatch('monitorUser', state.user)

    return personError() || userError() || monitorUser()
  },

  async getUserRecords(context, { catcher, firebaseAuthUser }) {
    const { email, providerData: [googleProfile] } = firebaseAuthUser

    const firestoreUser = await firecollection.get('person', email, catcher)

    return { firestoreUser, googleProfile }
  },

  async signIn({ commit, dispatch }, { action, firebaseAuthUser }) {
    commit(actionNames.SIGN_IN_REQUESTED, firebaseAuthUser.email)

    const analyticsEvent = 'Sign In'

    fireanalytics.event('goal_initialization', {
      category: 'Auth',
      description: analyticsEvent,
      label: `${analyticsEvent} Requested via ${action}`,
    })

    const catcher = (err) => {
      const error = http.catch(err)

      commit(actionNames.SIGN_IN_FAILED, error)

      fireanalytics.event('exception', {
        category: 'Auth',
        description: analyticsEvent,
        label: `${analyticsEvent} via ${action} Failed with Error "${error.message}"`,
      })

      return error
    }

    fireanalytics.event('update_user', {
      category: 'Auth',
      description: 'Sign In',
      label: 'Store User Requested',
    })

    const { firestoreUser, googleProfile } = await dispatch('getUserRecords', { catcher, firebaseAuthUser })

    commit('firebaseAuthUser', firebaseAuthUser)
    commit('firestoreUser', firestoreUser)
    commit('googleProfile', googleProfile)

    const storedUser = await dispatch('makeAndStoreUser', catcher)

    if (storedUser) {
      commit(actionNames.SIGN_IN_SUCCEEDED, storedUser)

      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Sign In',
        label: `Sign In via ${action} Succeeded`,
      })
    }

    return storedUser
  },
  async signOut({ commit, dispatch, state }, action = 'Unknown') {
    commit(actionNames.SIGN_OUT_REQUESTED)

    fireanalytics.event('goal_initialization', {
      category: 'Auth',
      description: 'Sign Out',
      label: `Sign Out Requested via ${action}`,
    })

    const catcher = (err) => {
      const error = http.catch(err)

      commit(actionNames.SIGN_OUT_FAILED, error)

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Sign Out',
        label: `Sign Out via ${action} Failed with Error "${error.message}"`,
      })

      return error
    }

    if (state.fnUnwatchUser) dispatch('unwatchUser')

    await fireauth.signOut().catch(catcher)

    commit(actionNames.SIGN_OUT_SUCCEEDED)

    http.clearAuthToken()

    fireanalytics.event('goal_completion', {
      category: 'Auth',
      description: 'Sign Out',
      label: `Sign Out via ${action} Succeeded`,
    })

    return true
  },
  async autoAuth({ commit, dispatch }, firebaseAuthUser) {
    commit(actionNames.AUTH_CHECK_REQUESTED)

    const analyticsCategory = 'Auth'

    const haveUser = firebaseAuthUser && typeof firebaseAuthUser === 'object'
    const analyticsEvent = `Sign ${(haveUser && 'In') || 'Out'}`
    const action = 'Page Load'

    dispatch('eventInitialized', { category: analyticsCategory, description: 'Auto Auth', label: `Attempt to ${analyticsEvent} via ${action} Requested` })

    const signIn = () => haveUser && dispatch('signIn', { action, firebaseAuthUser })
    const signOut = () => dispatch('signOut', action)

    await (signIn() || signOut())

    dispatch('eventCompleted', { category: analyticsCategory, description: 'Auto Auth', label: `Attempt to ${analyticsEvent} via ${action} Completed` })

    commit(actionNames.AUTO_AUTH_COMPLETE)
  },

  isShowingHelp(context, payload) {
    context.commit(actionNames.SHOW_HELP, payload)
  },
  getHelp(context) {
    context.commit(actionNames.SHOW_HELP, true)
  },
  hideHelp(context) {
    context.commit(actionNames.SHOW_HELP, false)
  },
}

export default actions
